import { ArrowRight, ChevronRight, TrendingUp, Car, Star, Shield, Zap, Users } from 'lucide-react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function LandingPage({ onGetStarted: _onGetStarted }: { onGetStarted: () => void }) {
  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* ── Live activity ticker ───────────────────────────────────────── */}
      <div className="bg-indigo-50 border-b border-indigo-100 py-2.5 overflow-hidden">
        <div className="flex items-center gap-3 px-4">
          <span className="flex-shrink-0 flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-white px-3 py-1 rounded-full border border-indigo-200">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            LIVE
          </span>
          <div className="overflow-hidden flex-1">
            <div className="flex gap-10 animate-scroll whitespace-nowrap">
              {[
                { name: 'Jordan M.', action: 'backed', car: 'Toyota Camry', pct: '74% funded', color: '#F97316' },
                { name: 'Priya S.', action: 'invested in', car: 'Honda CR-V', pct: 'Fully funded', color: '#059669' },
                { name: 'Marcus T.', action: 'gifted $500 to', car: 'Tesla Model 3', pct: '89% funded', color: '#F97316' },
                { name: 'Aaliyah R.', action: 'backed', car: 'Ford Explorer', pct: '50% funded', color: '#7C3AED' },
                { name: 'Devon K.', action: 'invested in', car: 'Chevy Malibu', pct: '63% funded', color: '#F97316' },
                { name: 'Tanya W.', action: 'gifted to', car: 'Kia Sportage', pct: 'Fully funded', color: '#059669' },
                { name: 'Jordan M.', action: 'backed', car: 'Toyota Camry', pct: '74% funded', color: '#F97316' },
                { name: 'Priya S.', action: 'invested in', car: 'Honda CR-V', pct: 'Fully funded', color: '#059669' },
                { name: 'Marcus T.', action: 'gifted $500 to', car: 'Tesla Model 3', pct: '89% funded', color: '#F97316' },
                { name: 'Aaliyah R.', action: 'backed', car: 'Ford Explorer', pct: '50% funded', color: '#7C3AED' },
              ].map((item, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                  <span className="font-semibold text-gray-800">{item.name}</span>
                  {item.action} ·
                  <span className="text-gray-600">{item.car}</span> ·
                  <span className="font-bold" style={{ color: item.color }}>{item.pct}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-[92vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1617469767133-3bc99bfadc10?w=1800&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg,rgba(79,70,229,0.96) 0%,rgba(124,58,237,0.88) 55%,rgba(219,39,119,0.6) 100%)' }} />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 pt-8 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* ── Left: headline + CTAs ── */}
            <div>
              <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/30 mb-10"
                style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
                <div className="flex -space-x-1.5">
                  {['https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&q=80',
                    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&q=80',
                    'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=40&q=80'].map((src, i) => (
                    <img key={i} src={src} alt="" className="w-6 h-6 rounded-full border-2 border-indigo-600 object-cover" />
                  ))}
                </div>
                <span className="text-sm text-white font-medium">Join 1,000+ backers already on FAM</span>
              </div>

              <h1 className="text-6xl sm:text-7xl font-extrabold leading-[1.02] tracking-tight mb-6 text-white">
                Be the reason
                <br />someone drives
                <br />
                <span style={{ background: 'linear-gradient(90deg,#fbbf24,#fb923c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  home.
                </span>
              </h1>

              <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-white/30 mb-6"
                style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
                <TrendingUp className="w-4 h-4 text-yellow-300 flex-shrink-0" />
                <span className="text-sm text-white">
                  <span className="font-bold text-yellow-300">$45,000</span>
                  <span className="opacity-80"> raised by </span>
                  <span className="font-bold text-orange-300">23 backers</span>
                  <span className="opacity-60"> — Tesla Model 3 campaign</span>
                </span>
              </div>

              <p className="text-lg text-white/85 leading-relaxed mb-10 max-w-lg">
                Browse live campaigns from real people in your community. Gift to support someone you care about, or invest for fractional ownership in a vehicle.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a href="/campaigns"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all hover:scale-105"
                  style={{ background: 'white', color: '#4F46E5', boxShadow: '0 6px 32px rgba(0,0,0,0.25)', minWidth: '220px' }}>
                  Browse Live Campaigns <ArrowRight className="w-5 h-5" />
                </a>
                <a href="https://funding.fam.autos"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white border border-white/40 hover:bg-white/20 transition-all">
                  Need funding? Apply here <ChevronRight className="w-4 h-4" />
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-8 mt-16 pt-10 border-t border-white/20">
                {[
                  { icon: Users, val: '$50M+', label: 'Community capital deployed' },
                  { icon: Car, val: '1,000+', label: 'Vehicles funded' },
                  { icon: TrendingUp, val: '98%', label: 'Campaigns succeed' },
                ].map(({ icon: Icon, val, label }) => (
                  <div key={label} className="flex items-center gap-2.5">
                    <Icon className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                    <div>
                      <div className="text-xl font-extrabold text-white leading-tight">{val}</div>
                      <div className="text-xs text-white/60">{label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: 3D hovering campaign card ── */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="relative">
                {/* Blur glow beneath card */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-72 h-10 rounded-full pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse,rgba(79,70,229,0.35) 0%,transparent 70%)', filter: 'blur(8px)' }} />

                <div className="animate-float3d pointer-events-none select-none w-80 rounded-2xl overflow-hidden bg-white"
                  style={{ boxShadow: '0 8px 48px rgba(79,70,229,0.2), 0 2px 8px rgba(0,0,0,0.1)' }}>

                  {/* Gradient accent bar */}
                  <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg,#4F46E5,#7C3AED,#DB2777)' }} />

                  {/* Vehicle image */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=640&q=80"
                      alt="Toyota Camry"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top,rgba(0,0,0,0.55) 0%,transparent 60%)' }} />
                    <span className="absolute bottom-3 left-3 text-xs font-bold px-2.5 py-1 rounded-full text-white bg-black/50 backdrop-blur-sm">
                      4 days left
                    </span>
                    <span className="absolute bottom-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full text-white"
                      style={{ background: '#059669' }}>
                      60% funded
                    </span>
                  </div>

                  <div className="p-4">
                    {/* Creator row */}
                    <div className="flex items-center gap-2.5 mb-3">
                      <img
                        src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80"
                        alt="Marcus T."
                        className="w-8 h-8 rounded-full object-cover border-2 border-indigo-100"
                      />
                      <div>
                        <p className="text-sm font-bold text-gray-900 leading-tight">Marcus T.</p>
                        <p className="text-xs text-gray-400">Houston, TX · 2022 Toyota Camry</p>
                      </div>
                      <div className="ml-auto flex items-center gap-1 text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                        Live
                      </div>
                    </div>

                    {/* Raised */}
                    <div className="flex items-end justify-between mb-1.5">
                      <span className="text-2xl font-extrabold text-gray-900">$17,100</span>
                      <span className="text-xs text-gray-400 mb-0.5">of $28,500 goal</span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-2 rounded-full bg-gray-100 mb-3">
                      <div className="h-2 rounded-full" style={{ width: '60%', background: 'linear-gradient(90deg,#4F46E5,#7C3AED,#DB2777)' }} />
                    </div>

                    {/* Activity bars */}
                    <div className="flex items-end gap-1 mb-3 h-8">
                      {[3,5,4,7,6,9,8,11,10,14,12,16,15,18].map((h, i) => (
                        <div key={i} className="flex-1 rounded-t"
                          style={{ height: `${(h / 18) * 100}%`, background: i >= 10 ? 'linear-gradient(180deg,#4F46E5,#7C3AED)' : '#E0E7FF', opacity: i >= 10 ? 1 : 0.7 }} />
                      ))}
                    </div>

                    {/* Backers row */}
                    <div className="flex items-center justify-between">
                      <div className="flex -space-x-2">
                        {[
                          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=40&q=80',
                          'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=40&q=80',
                          'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=40&q=80',
                        ].map((src, i) => (
                          <img key={i} src={src} alt="" className="w-6 h-6 rounded-full border-2 border-white object-cover" />
                        ))}
                        <div className="w-6 h-6 rounded-full border-2 border-white bg-indigo-100 flex items-center justify-center">
                          <span className="text-[9px] font-bold text-indigo-600">+12</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-400">15 backers</span>
                    </div>

                    {/* CTA buttons */}
                    <div className="flex gap-2 mt-3.5">
                      <div className="flex-1 py-2 rounded-xl text-center text-xs font-bold text-white"
                        style={{ background: 'linear-gradient(135deg,#F97316,#DC2626)' }}>
                        Gift
                      </div>
                      <div className="flex-1 py-2 rounded-xl text-center text-xs font-bold text-white"
                        style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)' }}>
                        Invest
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Two ways to back a campaign ───────────────────────────────── */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-3 text-gray-900">Two ways to back a campaign</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">Gift generously or invest strategically — the creator only ever sees the total raised, never the split.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl p-8 border border-orange-100 hover:border-orange-300 transition-all bg-white shadow-sm hover:shadow-md">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'linear-gradient(135deg,#F97316,#DC2626)', boxShadow: '0 4px 16px rgba(249,115,22,0.35)' }}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Gift</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">Contribute any amount as a pure gift. No return expected. A meaningful way to show up for someone in your community when they need it most.</p>
              <a href="/campaigns" className="block text-center py-3 rounded-xl font-semibold text-sm text-white hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg,#F97316,#DC2626)' }}>
                Find a Campaign →
              </a>
            </div>
            <div className="rounded-2xl p-8 border border-indigo-100 hover:border-indigo-300 transition-all bg-white shadow-sm hover:shadow-md">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)', boxShadow: '0 4px 16px rgba(79,70,229,0.35)' }}>
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Invest</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">Put money in and hold fractional LLC ownership of the vehicle. You hold paper on a real asset, earn returns on payments, and can vote on how it's managed.</p>
              <a href="/campaigns" className="block text-center py-3 rounded-xl font-semibold text-sm text-white hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)' }}>
                Invest in a Vehicle →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Campaigns you can back right now ──────────────────────────── */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-amber-200 text-sm text-amber-700 mb-6 bg-amber-50">
              {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />)}
              <span className="ml-1">Real people, real campaigns</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-3 text-gray-900">
              Campaigns you{' '}
              <span style={{ background: 'linear-gradient(90deg,#4F46E5,#7C3AED,#DB2777)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                can back right now.
              </span>
            </h2>
            <p className="text-gray-500 max-w-lg mx-auto">
              Every card below is a real person working toward their vehicle. Your support moves them closer.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                car: 'Tesla Model 3', year: '2023', goal: '$45,000', raised: '$40,500', pct: 90, supporters: 23,
                image: 'https://images.unsplash.com/photo-1617469767133-3bc99bfadc10?w=600&q=80',
                name: 'Sarah J.', city: 'Atlanta, GA',
                story: 'Single mom of two. My old car broke down and I can\'t get to work without wheels. This campaign is my last option before losing my job.',
                avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&q=80',
                daysLeft: 2, badge: '#059669',
              },
              {
                car: 'Toyota Camry', year: '2022', goal: '$28,500', raised: '$17,100', pct: 60, supporters: 15,
                image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=600&q=80',
                name: 'Marcus T.', city: 'Houston, TX',
                story: 'Just graduated. Got my first real job but no car to get there. Banks said no — three months post-graduation isn\'t a credit history.',
                avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80',
                daysLeft: 4, badge: '#F97316',
              },
              {
                car: 'Honda CR-V', year: '2023', goal: '$35,000', raised: '$7,000', pct: 20, supporters: 8,
                image: 'https://images.unsplash.com/photo-1619767886558-efdc259cde1a?w=600&q=80',
                name: 'Lisa R.', city: 'Chicago, IL',
                story: 'Three kids, two car seats, one minivan that keeps breaking down. Need something reliable before winter.',
                avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&q=80',
                daysLeft: 6, badge: '#4F46E5',
              },
            ].map((c) => (
              <a key={c.name} href="/campaigns"
                className="rounded-2xl overflow-hidden border border-gray-100 hover:border-indigo-200 transition-all block bg-white shadow-sm hover:shadow-lg">
                <div className="relative h-44 overflow-hidden">
                  <img src={c.image} alt={c.car} className="w-full h-full object-cover" />
                  <span className="absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full text-white bg-gray-900/70 backdrop-blur-sm">{c.daysLeft}d left</span>
                  <span className="absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full text-white" style={{ background: c.badge }}>{c.pct}% funded</span>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <p className="font-bold text-gray-900 text-base">{c.car}</p>
                      <p className="text-xs text-gray-400">{c.year} · {c.city}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-extrabold text-lg leading-tight" style={{ color: '#059669' }}>{c.raised}</p>
                      <p className="text-xs text-gray-400">of {c.goal}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-gray-400 mt-3 mb-1.5">
                    <Users className="w-3 h-3 mr-1" /> {c.supporters} backers
                  </div>
                  <div className="w-full h-1.5 rounded-full mb-4 bg-gray-100">
                    <div className="h-1.5 rounded-full" style={{ width: `${c.pct}%`, background: 'linear-gradient(90deg,#4F46E5,#7C3AED,#DB2777)' }} />
                  </div>
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-sm text-gray-500 leading-relaxed italic mb-4 line-clamp-2">"{c.story}"</p>
                    <div className="flex items-center gap-2.5">
                      <img src={c.avatar} alt={c.name} className="w-7 h-7 rounded-full object-cover border border-gray-200" />
                      <p className="text-sm font-semibold text-gray-800">{c.name}</p>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-10">
            <a href="/campaigns"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-white text-sm hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED,#DB2777)', boxShadow: '0 4px 20px rgba(79,70,229,0.35)' }}>
              See all live campaigns <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-14 rounded-2xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 bg-gray-50">
            {[
              { val: '1,000+', label: 'Vehicles backed', color: '#4F46E5' },
              { val: '$50M+', label: 'Community capital deployed', color: '#059669' },
              { val: '98%', label: 'Campaigns funded successfully', color: '#DC2626' },
            ].map(({ val, label, color }) => (
              <div key={label} className="py-8 text-center">
                <div className="text-4xl font-extrabold mb-1" style={{ color }}>{val}</div>
                <div className="text-sm text-gray-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How FAM works ─────────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-14">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium text-indigo-700 border border-indigo-200 mb-6 bg-indigo-50">
              How FAM works
            </span>
            <h2 className="text-4xl sm:text-5xl font-extrabold leading-tight text-gray-900">
              Community capital,
              <br />
              <span style={{ background: 'linear-gradient(90deg,#4F46E5,#7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                real returns.
              </span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-md text-sm leading-relaxed">
              FAM puts you on the other side of auto financing — as the capital that makes it happen.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-10">
            {[
              { n: '01', icon: Car, title: 'Browse campaigns', body: 'Every campaign lists the vehicle, the creator\'s story, and how far along they are. Pick the ones you believe in.' },
              { n: '02', icon: Shield, title: 'Gift or invest', body: 'Gift any amount as pure support — or invest for fractional LLC ownership. The creator sees total raised, never the split.' },
              { n: '03', icon: TrendingUp, title: 'FAM buys the car', body: 'When the campaign closes, FAM underwrites and buys the vehicle outright. Investors hold paper on a real asset.' },
            ].map(({ n, icon: Icon, title, body }, i) => (
              <div key={n} className="relative">
                <div className="relative inline-block mb-5">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg,#F97316,#DC2626)', boxShadow: '0 6px 24px rgba(249,115,22,0.4)' }}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold text-white border-2 border-white"
                    style={{ background: '#4F46E5' }}>
                    {i + 1}
                  </span>
                </div>
                <span className="text-6xl font-extrabold text-gray-100 leading-none block mb-2 -mt-1 select-none">{n}</span>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
                {i < 2 && <ChevronRight className="hidden sm:block absolute top-8 -right-6 w-5 h-5 text-gray-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────── */}
      <section className="relative py-28 px-6 overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#4F46E5 0%,#7C3AED 50%,#DB2777 100%)' }}>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="absolute rounded-full pointer-events-none opacity-10"
            style={{ width: 140 + i * 30, height: 45, left: `${5 + i * 12}%`, top: `${15 + (i % 3) * 30}%`, background: 'white', transform: 'rotate(-8deg)' }} />
        ))}
        <div className="relative max-w-xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-8"
            style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}>
            <Car className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight text-white">
            Someone near you
            <br />needs a yes.
          </h2>
          <p className="text-white/80 mb-10 leading-relaxed">
            Browse live campaigns and be the community capital that gets someone off the waitlist and onto the road.
          </p>
          <a href="/campaigns"
            className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-base transition-all hover:scale-105 mb-6 bg-white"
            style={{ color: '#4F46E5', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
            Browse Live Campaigns <ArrowRight className="w-5 h-5" />
          </a>
          <div className="flex items-center justify-center gap-6 text-xs text-white/60 mt-2">
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Secured by Stripe</span>
            <span className="w-px h-3 bg-white/20" />
            <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Gift or invest any amount</span>
            <span className="w-px h-3 bg-white/20" />
            <span className="flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Real asset ownership</span>
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="border-t border-gray-100 py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#4F46E5,#7C3AED)' }}>
                <Car className="w-4 h-4 text-white" />
              </div>
              <span className="font-extrabold text-gray-900">FAM</span>
              <span className="text-gray-400 text-sm">Family &amp; More</span>
            </div>
            <nav className="flex items-center gap-6 text-sm text-gray-400">
              <a href="/privacy" className="hover:text-gray-700 transition-colors">Privacy</a>
              <a href="/campaigns" className="hover:text-gray-700 transition-colors">Campaigns</a>
              <a href="https://funding.fam.autos" className="hover:text-gray-700 transition-colors">Get Funded</a>
              <a href="https://dealers.fam.autos" className="hover:text-gray-700 transition-colors">Dealer Portal</a>
            </nav>
          </div>
          <div className="border-t border-gray-100 pt-6 text-center">
            <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} FAM. All rights reserved.</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
